import { useState, useEffect, useCallback } from 'react';
import { Calendar, Clock, User, FileText, X, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { usePatients } from '../../lib/hooks/usePatients';
import { apiClient } from '../../lib/api-client';
// ✅ FIX: Removed AppointmentType import - not needed anymore
import type { CareTeam } from '../../lib/api/care-teams';

interface CareTeamMember {
  id: number;
  name: string;
  role: 'Doctor' | 'Physician Assistant' | 'Therapist' | 'Nurse';  // ✅ Added PA
  specialization?: string;
  hospital_id?: number;
}

interface ScheduleAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function ScheduleAppointmentModal({ isOpen, onClose, onSuccess }: ScheduleAppointmentModalProps) {
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [selectedCareMembers, setSelectedCareMembers] = useState<CareTeamMember[]>([]);
  const [careTeamMembers, setCareTeamMembers] = useState<CareTeamMember[]>([]);
  const [loadingCareTeam, setLoadingCareTeam] = useState(false);
  
  const [formData, setFormData] = useState({
    scheduled_date: '',
    scheduled_time: '',
    // ✅ FIX: Remove appointment_type - all appointments are teleconsultation
    reason: '',
    notes: ''
  });

  const [submitting, setSubmitting] = useState(false);

  // Fetch PA's assigned patients
  const { patients: patientsList, loading: patientsLoading } = usePatients({ limit: 1000 });

  const patients = patientsList || [];

  // Memoized function to load care team members from care team (doctors, therapist, nurse)
  const loadCareTeamMembers = useCallback(async (patient: any) => {
    try {
      setLoadingCareTeam(true);
      
      // Check if patient has a care team assigned
      if (!patient.care_team_id) {
        toast.error('This patient is not assigned to any care team');
        setCareTeamMembers([]);
        return;
      }
      
      // Fetch the patient's care team
      const careTeamResponse = await apiClient.get(`/care-teams/${patient.care_team_id}`);
      
      console.log('📋 PA - Care team API response:', careTeamResponse);
      
      const members: CareTeamMember[] = [];
      
      // ✅ FIX: Add all doctors from care team (backend doesn't return hospital_id in nested objects)
      // The care team itself has hospital_id, so all members belong to that hospital
      if (careTeamResponse.doctors && Array.isArray(careTeamResponse.doctors)) {
        const doctors = careTeamResponse.doctors.map((doctor: any) => ({
          id: doctor.id,
          name: doctor.name,
          role: 'Doctor' as const,
          specialization: doctor.specialization,
          hospital_id: careTeamResponse.hospital_id // ✅ Use care team's hospital_id
        }));
        members.push(...doctors);
        console.log('✅ PA - Added doctors:', doctors.length);
      }
      
      // ✅ FIX: Add therapist from care team (all care team members are at same hospital)
      if (careTeamResponse.therapist) {
        members.push({
          id: careTeamResponse.therapist.id,
          name: careTeamResponse.therapist.name,
          role: 'Therapist' as const,
          specialization: careTeamResponse.therapist.specialization,
          hospital_id: careTeamResponse.hospital_id // ✅ Use care team's hospital_id
        });
        console.log('✅ PA - Added therapist');
      }
      
      // ✅ FIX: Add nurse from care team (all care team members are at same hospital)
      if (careTeamResponse.nurse) {
        members.push({
          id: careTeamResponse.nurse.id,
          name: careTeamResponse.nurse.name,
          role: 'Nurse' as const,
          specialization: careTeamResponse.nurse.specialization || 'Home Healthcare',
          hospital_id: careTeamResponse.hospital_id // ✅ Use care team's hospital_id
        });
        console.log('✅ PA - Added nurse');
      }
      
      // ✅ FIX: Add physician assistants if available
      if (careTeamResponse.physician_assistants && Array.isArray(careTeamResponse.physician_assistants)) {
        const pas = careTeamResponse.physician_assistants.map((pa: any) => ({
          id: pa.id,
          name: pa.name,
          role: 'Physician Assistant' as const,  // ✅ FIX: Use correct role
          specialization: pa.specialization || 'Physician Assistant',
          hospital_id: careTeamResponse.hospital_id
        }));
        members.push(...pas);
        console.log('✅ PA - Added physician assistants:', pas.length);
      }

      if (members.length === 0) {
        console.warn('⚠️ PA - No care team members found');
        toast.error('No care team members are assigned to this patient');
      }

      setCareTeamMembers(members);
      console.log('✅ PA - Total care team members loaded:', members.length, members);
      
    } catch (error: any) {
      console.error('Failed to load care team:', error);
      toast.error('Failed to load care team members');
      setCareTeamMembers([]);
    } finally {
      setLoadingCareTeam(false);
    }
  }, []); // Empty dependency array since it only uses state setters

  // Load care team members when patient is selected
  useEffect(() => {
    if (selectedPatient) {
      loadCareTeamMembers(selectedPatient);
    } else {
      setCareTeamMembers([]);
      setSelectedCareMembers([]);
    }
  }, [selectedPatient, loadCareTeamMembers]);

  const handlePatientSelect = (patientId: string) => {
    const patient = patients.find((p: any) => p.id === parseInt(patientId));
    setSelectedPatient(patient || null);
    // Reset care member selection when patient changes
    setSelectedCareMembers([]);
  };

  const toggleCareTeamMember = (member: CareTeamMember) => {
    setSelectedCareMembers(prev => {
      const isSelected = prev.some(m => m.id === member.id && m.role === member.role);
      if (isSelected) {
        return prev.filter(m => !(m.id === member.id && m.role === member.role));
      } else {
        return [...prev, member];
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!selectedPatient) {
      toast.error('Please select a patient');
      return;
    }

    if (selectedCareMembers.length === 0) {
      toast.error('Please select at least one care team member');
      return;
    }

    if (!formData.scheduled_date || !formData.scheduled_time) {
      toast.error('Please select date and time');
      return;
    }

    if (!formData.reason) {
      toast.error('Please enter reason for visit');
      return;
    }

    // Combine date and time
    const scheduledDateTime = `${formData.scheduled_date}T${formData.scheduled_time}:00`;

    try {
      setSubmitting(true);

      // Extract IDs by role
      const doctorIds = selectedCareMembers.filter(m => m.role === 'Doctor').map(m => m.id);
      const paIds = selectedCareMembers.filter(m => m.role === 'Physician Assistant').map(m => m.id);
      const therapistIds = selectedCareMembers.filter(m => m.role === 'Therapist').map(m => m.id);
      const nurseIds = selectedCareMembers.filter(m => m.role === 'Nurse').map(m => m.id);

      // ✅ FIX: Send correct IDs to respective fields
      const appointmentData = {
        patient_id: selectedPatient.id,
        doctor_id: doctorIds[0] || undefined,  // Primary doctor
        physician_assistant_id: paIds[0] || undefined,  // ✅ NEW: PA assignment
        therapist_id: therapistIds[0] || undefined,
        nurse_id: nurseIds[0] || undefined,
        hospital_id: selectedPatient.hospital_id || undefined, // Include hospital if patient has one
        // appointment_type removed - defaults to Teleconsultation in backend
        scheduled_date: scheduledDateTime,
        duration_minutes: 30,
        title: formData.reason, // Use reason as title
        description: formData.notes || undefined,
        meeting_link: undefined // Will be generated if needed
      };

      await apiClient.post('/appointments', appointmentData);

      toast.success(`Appointment scheduled with ${selectedCareMembers.length} care team member(s)`);
      
      resetForm();
      onClose();
      onSuccess?.();
    } catch (error: any) {
      console.error('Failed to create appointment:', error);
      toast.error(error.message || 'Failed to schedule appointment');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedPatient(null);
    setSelectedCareMembers([]);
    setCareTeamMembers([]);
    setFormData({
      scheduled_date: '',
      scheduled_time: '',
      // ✅ FIX: Remove appointment_type from reset
      reason: '',
      notes: ''
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  const patientsCount = patients.length;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-primary">
              <Calendar className="w-5 h-5 text-primary" />
              Schedule New Appointment
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="h-6 w-6 rounded-full"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Select patient from your assigned list. Care team members (doctors, therapist, nurse) at patient's hospital will be shown for multi-selection.
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-5">
          {/* Step 1: Select Patient & Doctor */}
          <div className="bg-orange-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 text-primary">
              <User className="w-5 h-5" />
              <Label className="font-semibold">Step 1: Select Patient & Care Team</Label>
            </div>
            
            <div className="space-y-3">
              {/* Patient */}
              <div className="space-y-2">
                <Label htmlFor="patient" className="text-sm">Patient <span className="text-red-500">*</span></Label>
                <Select
                  value={selectedPatient?.id.toString() || ''}
                  onValueChange={handlePatientSelect}
                >
                  <SelectTrigger id="patient" className="bg-gray-100 border-gray-200">
                    <SelectValue placeholder="Select patient" />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.length === 0 ? (
                      <div className="p-2 text-sm text-gray-500">No patients assigned</div>
                    ) : (
                      patients.map((patient: any) => (
                        <SelectItem key={patient.id} value={patient.id.toString()}>
                          <div className="flex flex-col">
                            <span className="font-medium">{patient.name}</span>
                            <span className="text-xs text-gray-500">UMR: {patient.umr_no}</span>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <div className="text-xs text-gray-500">
                  {patientsCount} {patientsCount === 1 ? 'patient' : 'patients'} assigned to you
                </div>
              </div>

              {/* Care Team Members (Multi-Select) */}
              <div className="space-y-2">
                <Label className="text-sm">Care Team Members <span className="text-red-500">*</span></Label>
                <div className="text-xs text-gray-500 mb-2">
                  Select one or more: Doctors, Therapist, Nurse
                </div>
                
                {loadingCareTeam ? (
                  <div className="p-4 text-sm text-gray-500 bg-gray-100 rounded border">Loading care team...</div>
                ) : !selectedPatient ? (
                  <div className="p-4 text-sm text-gray-500 bg-gray-100 rounded border">Select a patient first</div>
                ) : careTeamMembers.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500 bg-gray-100 rounded border">
                    No care team members available
                  </div>
                ) : (
                  <div className="bg-gray-100 rounded border divide-y max-h-60 overflow-y-auto">
                    {careTeamMembers.map((member) => {
                      const isSelected = selectedCareMembers.some(m => m.id === member.id && m.role === member.role);
                      return (
                        <div
                          key={`${member.role}-${member.id}`}
                          className={`p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                            isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                          }`}
                          onClick={() => toggleCareTeamMember(member)}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">
                                {member.role === 'Doctor' ? 'Dr. ' : ''}{member.name}
                              </span>
                              <Badge variant={
                                member.role === 'Doctor' ? 'default' : 
                                member.role === 'Therapist' ? 'secondary' : 
                                'outline'
                              } className="text-xs">
                                {member.role}
                              </Badge>
                            </div>
                            {member.specialization && (
                              <div className="text-xs text-gray-500 mt-1">{member.specialization}</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {selectedCareMembers.length > 0 && (
                  <div className="text-xs text-green-600 mt-2">
                    ✓ {selectedCareMembers.length} member{selectedCareMembers.length > 1 ? 's' : ''} selected
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Step 2: Appointment Details */}
          {selectedPatient && selectedCareMembers.length > 0 && (
            <div className="bg-cyan-50 rounded-lg p-4 space-y-4">
              <div className="flex items-center gap-2 text-primary">
                <Clock className="w-5 h-5" />
                <Label className="font-semibold">Step 2: Appointment Details</Label>
              </div>

              {/* ✅ FIX: Appointment Type field removed - all appointments are teleconsultation */}
              {/* Information badge */}
              <div className="bg-cyan-100 border border-cyan-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-cyan-500"></div>
                  <span className="text-sm text-cyan-800">
                    All appointments are <strong>Teleconsultation</strong> appointments
                  </span>
                </div>
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="scheduled_date" className="text-sm">
                    Date <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="scheduled_date"
                    type="date"
                    min={today}
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                    className="bg-gray-100 border-gray-200"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="scheduled_time" className="text-sm">
                    Time <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="scheduled_time"
                    type="time"
                    value={formData.scheduled_time}
                    onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                    className="bg-gray-100 border-gray-200"
                    required
                  />
                </div>
              </div>

              {/* Reason for Visit */}
              <div className="space-y-2">
                <Label htmlFor="reason" className="text-sm">
                  Reason for Visit <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="reason"
                  placeholder="Enter reason for appointment"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  rows={3}
                  className="bg-gray-100 border-gray-200 resize-none"
                  required
                />
              </div>

              {/* Notes (Optional) */}
              <div className="space-y-2">
                <Label htmlFor="notes" className="text-sm">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Additional notes or special instructions"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="bg-gray-100 border-gray-200 resize-none"
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {selectedPatient && selectedCareMembers.length > 0 && (
            <div className="flex justify-between pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting || !formData.scheduled_date || !formData.scheduled_time || !formData.reason}
                className="bg-primary hover:bg-primary/90"
              >
                {submitting ? 'Scheduling...' : 'Schedule Appointment'}
              </Button>
            </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
